// src/data/checkPages.js
// Pages « vérification » : une page par contrôle effectué par le scanner.
// Fichier sans import : il est aussi lu par scripts/prerender.mjs.
// Pour ajouter une page (SSL, en-têtes HTTP...), dupliquez l'objet et adaptez le contenu.
// Les éléments de "checks" et "example" doivent rester fidèles à ce que fait
// réellement perform_dns_scan() dans backend/server.py.

const checkPages = [
  {
    slug: "verifier-dmarc-spf-dkim",
    navLabel: "DMARC / SPF / DKIM",
    seoTitle: "Vérifier DMARC, SPF et DKIM de votre domaine | CyberGuard",
    description:
      "Votre domaine peut-il être usurpé par email ? CyberGuard vérifie SPF, DKIM, DMARC et MX en 2 minutes et vous dit quoi corriger. Dès 29 €/mois.",
    kicker: "Sécurité email",
    h1: "Vérifiez SPF, DKIM et DMARC de votre domaine",
    intro: [
      "Sans ces trois réglages, n'importe qui peut envoyer un email avec votre adresse : une fausse facture « de votre part », un faux changement de RIB envoyé à vos clients.",
      "CyberGuard lit les enregistrements DNS publics de votre domaine et vous dit en 2 minutes ce qui manque et comment le corriger.",
    ],
    cta: "Tester mon domaine",

    sections: [
      {
        title: "SPF, DKIM, DMARC : à quoi ça sert",
        items: [
          {
            term: "SPF",
            text: "La liste des serveurs autorisés à envoyer des emails au nom de votre domaine. C'est un enregistrement DNS de type TXT qui commence par v=spf1.",
          },
          {
            term: "DKIM",
            text: "Une signature numérique ajoutée à chaque email, qui prouve qu'il vient bien de vous et n'a pas été modifié en route. La clé publique est publiée dans le DNS, sous un « sélecteur » (par exemple google._domainkey.votre-domaine.fr).",
          },
          {
            term: "DMARC",
            text: "La règle qui dit aux messageries quoi faire d'un email qui échoue aux contrôles SPF et DKIM : le laisser passer (p=none), le mettre en spam (p=quarantine) ou le rejeter (p=reject). Elle est publiée sur _dmarc.votre-domaine.fr.",
          },
        ],
        note: "Depuis février 2024, Gmail et Yahoo demandent au minimum SPF ou DKIM à tous les expéditeurs, et les trois protections à ceux qui envoient en masse. Un domaine mal configuré voit aussi ses emails légitimes finir plus souvent en spam.",
      },
    ],

    checksTitle: "Ce que CyberGuard vérifie",
    checks: [
      "SPF : présence de l'enregistrement et politique finale (-all strict, ~all souple, ou absente).",
      "DMARC : présence sur _dmarc et niveau de la politique (none, quarantine ou reject).",
      "DKIM : recherche d'une clé sur les sélecteurs les plus courants, dont ceux de Google Workspace et Microsoft 365 (default, google, mail, dkim, k1, selector1, selector2).",
      "MX : présence des serveurs qui reçoivent vos emails.",
    ],
    checksNote:
      "Si votre prestataire utilise un sélecteur DKIM personnalisé, il peut ne pas être trouvé : le rapport l'indique alors comme « non détecté » plutôt que comme absent.",

    exampleTitle: "Exemple de résultat",
    exampleDomain: "exemple-pme.fr (domaine fictif)",
    example: [
      { status: "error", text: "DMARC manquant — emails frauduleux non bloqués" },
      { status: "warning", text: "SPF configuré en mode souple (~all) — préférez -all" },
      { status: "ok", text: "DKIM configuré (sélecteur : google)" },
      { status: "ok", text: "Serveurs MX configurés : aspmx.l.google.com" },
    ],
    exampleNote:
      "Chaque point pèse sur votre score de sécurité sur 100. Dès le plan Pro, le rapport IA vous donne le guide de correction adapté à votre domaine, à transmettre tel quel à votre prestataire.",

    fixTitle: "Comment corriger, dans l'ordre",
    fixSteps: [
      {
        title: "Recensez tout ce qui envoie des emails pour vous",
        text: "Messagerie, outil de newsletter, CRM, logiciel de facturation, site web. Chacun doit apparaître dans un seul et unique enregistrement SPF : deux enregistrements v=spf1 sur le même domaine rendent SPF invalide. SPF est aussi limité à 10 consultations DNS, d'où l'intérêt de ne garder que les services réellement utilisés.",
      },
      {
        title: "Activez DKIM chez votre messagerie",
        text: "Dans la console d'administration de Google Workspace ou de Microsoft 365, activez la signature DKIM et publiez dans votre DNS la clé qu'elle vous fournit. Faites de même pour votre outil de newsletter s'il le propose.",
      },
      {
        title: "Publiez DMARC en mode surveillance, puis durcissez",
        text: "Commencez par v=DMARC1; p=none avec une adresse rua=mailto:... pour recevoir les rapports. Quand ils montrent que vos envois légitimes passent, montez à p=quarantine, puis à p=reject.",
      },
    ],
    fixWarning:
      "Passer directement à p=reject sans cette étape de surveillance peut bloquer vos propres emails, comme vos newsletters ou vos factures envoyées par un logiciel tiers.",

    faqs: [
      {
        q: "Mon domaine n'envoie pas d'emails. Suis-je concerné ?",
        a: "Oui. Un domaine sans SPF ni DMARC est encore plus facile à usurper. Pour un domaine qui n'envoie rien, publiez v=spf1 -all et une politique DMARC p=reject : toute tentative d'envoi en son nom sera refusée.",
      },
      {
        q: "Le test modifie-t-il quelque chose sur mon domaine ?",
        a: "Non. CyberGuard lit uniquement les enregistrements DNS publics, comme le fait n'importe quel serveur de messagerie qui reçoit un de vos emails. Rien n'est envoyé ni modifié.",
      },
      {
        q: "Combien de temps une correction met-elle à être prise en compte ?",
        a: "Les modifications DNS se propagent en général en quelques minutes à quelques heures, selon la durée de vie (TTL) configurée chez votre hébergeur DNS. Vous pouvez relancer un audit ensuite pour vérifier.",
      },
      {
        q: "Quel plan faut-il pour cette analyse ?",
        a: "L'analyse SPF, DKIM, DMARC et MX est incluse dès le plan Starter à 29 €/mois. Le rapport IA et le guide de correction détaillé sont inclus à partir du plan Pro à 79 €/mois.",
      },
    ],

    related: [
      { label: "Comment configurer DMARC : le guide pas à pas pour les PME", href: "/blog/configurer-dmarc-guide-pme" },
      { label: "SPF : comprendre et configurer l'enregistrement qui protège vos emails", href: "/blog/comprendre-spf-email" },
      { label: "Usurpation de domaine par email : les trois formes d'arnaque et comment s'en protéger", href: "/blog/usurpation-domaine-email-pme" },
      { label: "Phishing en entreprise : pourquoi les PME sont les plus exposées", href: "/blog/phishing-pme-comment-se-proteger" },
    ],
  },
  {
    slug: "verifier-certificat-ssl",
    navLabel: "Certificat SSL",
    seoTitle: "Vérifier le certificat SSL de votre site | CyberGuard",
    description:
      "Certificat expiré, HTTPS mal redirigé, HSTS absent ? CyberGuard vérifie le SSL de votre site en 2 minutes et vous dit quoi corriger. Dès 29 €/mois.",
    kicker: "Sécurité web",
    h1: "Vérifiez le certificat SSL et le HTTPS de votre site",
    intro: [
      "Un certificat expiré ou mal installé, et les navigateurs affichent « Votre connexion n'est pas privée » : la plupart des visiteurs repartent aussitôt. Sans HTTPS valide, les données saisies dans vos formulaires circulent aussi sans protection.",
      "CyberGuard se connecte à votre site comme un navigateur et vérifie en 2 minutes que le chiffrement est en place, de la redirection jusqu'au certificat.",
    ],
    cta: "Tester mon site",
    ctaTitle: "Votre site est-il bien protégé ?",

    sections: [
      {
        title: "SSL, HTTPS, HSTS, CAA : de quoi parle-t-on",
        items: [
          {
            term: "Certificat SSL/TLS",
            text: "Le fichier qui prouve l'identité de votre site et permet de chiffrer les échanges avec vos visiteurs. Il est délivré par une autorité de certification (Let's Encrypt, Sectigo...) pour un nom de domaine précis et pour une durée limitée.",
          },
          {
            term: "Redirection HTTPS",
            text: "Un visiteur qui tape votre adresse sans « https:// » doit être renvoyé automatiquement vers la version sécurisée. Sinon, il navigue sur une version non chiffrée de votre site.",
          },
          {
            term: "HSTS",
            text: "Un en-tête (Strict-Transport-Security) qui demande au navigateur de n'utiliser que HTTPS pour votre site pendant une durée donnée, même si on lui donne un lien en http://. Il bloque les attaques qui tentent de faire basculer la connexion en clair.",
          },
          {
            term: "CAA",
            text: "Un enregistrement DNS qui indique quelles autorités ont le droit de délivrer un certificat pour votre domaine. Il empêche qu'une autre autorité en émette un à votre insu.",
          },
        ],
        note: "La durée de vie des certificats raccourcit : depuis mars 2026, un nouveau certificat ne peut plus être valable plus de 200 jours ; ce sera 100 jours en 2027, puis 47 jours en 2029. Un renouvellement automatique n'est plus une option.",
      },
    ],

    checksTitle: "Ce que CyberGuard vérifie",
    checks: [
      "Certificat : connexion HTTPS établie avec vérification complète, comme le ferait un navigateur. Un certificat expiré, auto-signé ou émis pour un autre nom de domaine est signalé.",
      "Date d'expiration : nombre de jours restants, avec un avertissement à moins de 30 jours et une alerte critique à moins de 7 jours.",
      "Surveillance continue (plans Pro et Enterprise) : chaque matin, CyberGuard relit les certificats des domaines que vous avez audités ces 90 derniers jours et vous prévient par email à 30, 14, 7 puis 1 jour de l'expiration.",
      "Redirection : l'adresse en http:// renvoie bien vers https://.",
      "HSTS : présence de l'en-tête Strict-Transport-Security.",
      "CAA : présence d'un enregistrement CAA sur le domaine.",
      "Protocoles (plans Pro et Enterprise, domaine vérifié) : CyberGuard tente une connexion en TLS 1.0 et 1.1, deux versions obsolètes que votre serveur devrait refuser, et confirme la prise en charge de TLS 1.3.",
    ],
    checksNote:
      "Un renouvellement automatique peut échouer sans prévenir : un changement de DNS, une carte bancaire expirée chez l'hébergeur, un certificat renouvelé mais pas installé. La surveillance quotidienne détecte ces cas avant vos visiteurs.",

    exampleTitle: "Exemple de résultat",
    exampleDomain: "exemple-pme.fr (domaine fictif)",
    example: [
      { status: "ok", text: "SSL/TLS configuré et valide" },
      { status: "warning", text: "Certificat SSL expire dans 12 jours (le 26/10/2026)" },
      { status: "warning", text: "Pas de redirection HTTP vers HTTPS" },
      { status: "warning", text: "HSTS manquant" },
      { status: "info", text: "CAA non configuré" },
    ],
    exampleNote:
      "Chaque point pèse sur votre score de sécurité sur 100 ; un certificat invalide est le défaut le plus lourd. Dès le plan Pro, le rapport IA vous donne le guide de correction adapté à votre hébergement, à transmettre tel quel à votre prestataire.",

    fixTitle: "Comment corriger, dans l'ordre",
    fixSteps: [
      {
        title: "Activez un certificat à renouvellement automatique",
        text: "La plupart des hébergeurs proposent un certificat Let's Encrypt gratuit, renouvelé automatiquement. Activez-le dans le panneau de votre hébergeur et vérifiez qu'il couvre aussi la version www de votre domaine.",
      },
      {
        title: "Forcez la redirection vers HTTPS",
        text: "Cochez l'option « Forcer HTTPS » de votre hébergeur, ou ajoutez une redirection permanente (301) de http:// vers https://. Vérifiez ensuite que les images et les scripts de vos pages sont eux aussi chargés en https://.",
      },
      {
        title: "Ajoutez HSTS, puis un enregistrement CAA",
        text: "Une fois tout le site en HTTPS, ajoutez l'en-tête Strict-Transport-Security: max-age=31536000. Côté DNS, un enregistrement CAA du type 0 issue \"letsencrypt.org\" réserve l'émission de certificats à votre autorité.",
      },
    ],
    fixWarning:
      "N'activez pas HSTS avant d'avoir vérifié que tout votre site fonctionne en HTTPS : les navigateurs refuseront toute connexion en http:// pendant la durée indiquée, et l'option includeSubDomains étend cette règle à tous vos sous-domaines. Commencez par une durée courte (une journée), puis allongez-la.",

    faqs: [
      {
        q: "Un certificat Let's Encrypt gratuit est-il aussi sûr qu'un certificat payant ?",
        a: "Pour le chiffrement, oui : la protection est la même. Les certificats payants apportent surtout une vérification plus poussée de l'entreprise ou une garantie commerciale, rarement utiles pour le site d'une PME.",
      },
      {
        q: "Mon navigateur affiche le cadenas. Pourquoi CyberGuard signale-t-il un problème ?",
        a: "Le cadenas confirme seulement que la page affichée est chiffrée. Il ne dit pas si la version http:// redirige bien, si HSTS est en place ni si votre domaine est protégé par un enregistrement CAA.",
      },
      {
        q: "Le test est-il intrusif ?",
        a: "Non. CyberGuard se connecte à votre site exactement comme un visiteur et lit la page d'accueil et ses en-têtes. Aucune attaque n'est simulée et rien n'est modifié.",
      },
      {
        q: "Quel plan faut-il pour cette analyse ?",
        a: "La vérification du certificat, de sa date d'expiration, de la redirection HTTPS, de HSTS et de CAA est incluse dès le plan Starter à 29 €/mois. Les alertes email avant expiration, le rapport IA et le guide de correction détaillé sont inclus à partir du plan Pro à 79 €/mois.",
      },
    ],

    related: [{ label: "Pentest ou scan de vulnérabilités : lequel choisir (et quand) ?", href: "/blog/pentest-vs-scan-vulnerabilites" }],
  },
  {
    slug: "tester-en-tetes-http-securite",
    navLabel: "En-têtes HTTP",
    seoTitle: "Tester les en-têtes de sécurité HTTP | CyberGuard",
    description:
      "HSTS, CSP, X-Frame-Options : CyberGuard teste les en-têtes de sécurité de votre site en 2 minutes et vous explique quoi corriger. Dès 29 €/mois.",
    kicker: "Sécurité web",
    h1: "Testez les en-têtes de sécurité HTTP de votre site",
    intro: [
      "Les en-têtes HTTP sont des consignes que votre serveur envoie au navigateur avec chaque page : ne charger le site qu'en HTTPS, refuser d'être affiché dans un cadre sur un autre site, n'exécuter que les scripts autorisés. Ils ne coûtent rien et bloquent des attaques courantes, mais ils sont souvent oubliés.",
      "CyberGuard lit les en-têtes de votre site en 2 minutes et vous dit lesquels manquent.",
    ],
    cta: "Tester mon site",
    ctaTitle: "Votre site est-il bien protégé ?",

    sections: [
      {
        title: "Les en-têtes qui comptent",
        items: [
          {
            term: "Strict-Transport-Security (HSTS)",
            text: "Oblige le navigateur à n'utiliser que HTTPS pour votre site, même si on lui donne un lien en http://.",
          },
          {
            term: "Content-Security-Policy (CSP)",
            text: "La liste des sources autorisées pour les scripts, les styles et les images. C'est la principale protection contre l'injection de code malveillant dans vos pages (XSS).",
          },
          {
            term: "X-Frame-Options",
            text: "Empêche un autre site d'afficher le vôtre dans un cadre invisible pour piéger vos visiteurs (clickjacking).",
          },
          {
            term: "X-Content-Type-Options",
            text: "Avec la valeur nosniff, il interdit au navigateur de deviner le type d'un fichier, ce qui évite qu'un fichier anodin soit exécuté comme un script.",
          },
          {
            term: "Referrer-Policy",
            text: "Limite les informations sur la page d'origine transmises aux autres sites quand un visiteur clique sur un lien.",
          },
          {
            term: "Permissions-Policy",
            text: "Désactive les fonctions du navigateur dont votre site n'a pas besoin : caméra, micro, géolocalisation...",
          },
        ],
        note: "Ces en-têtes se configurent une fois pour toutes chez votre hébergeur ou dans votre serveur, sans toucher au contenu du site.",
      },
    ],

    checksTitle: "Ce que CyberGuard vérifie",
    checks: [
      "Dès le plan Starter : présence de Strict-Transport-Security, Content-Security-Policy et X-Frame-Options.",
      "À partir du plan Pro : X-Content-Type-Options (nosniff), Referrer-Policy et Permissions-Policy.",
      "À partir du plan Pro : version du serveur exposée dans l'en-tête Server (Apache, nginx, IIS, PHP, Express).",
      "À partir du plan Pro : CORS trop permissif (Access-Control-Allow-Origin: *) et cookies sans attributs Secure, HttpOnly ou SameSite.",
      "Plans Pro et Enterprise, sur un domaine vérifié : qualité de la CSP (scripts autorisés en 'unsafe-inline' ou 'unsafe-eval', sources joker, absence d'object-src).",
    ],
    checksNote:
      "Dès le plan Starter, CyberGuard vérifie que la CSP est présente. Une politique trop large, par exemple avec 'unsafe-inline', protège nettement moins : l'analyse de son contenu est faite sur les plans Pro et Enterprise, une fois votre domaine vérifié.",

    exampleTitle: "Exemple de résultat",
    exampleDomain: "exemple-pme.fr (domaine fictif, plan Pro)",
    example: [
      { status: "ok", text: "HSTS activé" },
      { status: "warning", text: "CSP manquant — risque XSS élevé" },
      { status: "warning", text: "X-Frame-Options manquant — risque clickjacking" },
      { status: "warning", text: "Version serveur exposée : Apache/2.4.41" },
      { status: "error", text: "CORS trop permissif (Allow-Origin: *)" },
    ],
    exampleNote:
      "Chaque point pèse sur votre score de sécurité sur 100. Dès le plan Pro, le rapport IA vous donne le guide de correction adapté à votre serveur, à transmettre tel quel à votre prestataire.",

    fixTitle: "Comment corriger, dans l'ordre",
    fixSteps: [
      {
        title: "Ajoutez d'abord les en-têtes simples",
        text: "X-Frame-Options: DENY (ou SAMEORIGIN), X-Content-Type-Options: nosniff, Referrer-Policy: strict-origin-when-cross-origin, et une Permissions-Policy qui coupe la caméra, le micro et la géolocalisation. Ils s'ajoutent dans le panneau de l'hébergeur, dans le fichier .htaccess (Apache), dans la configuration nginx ou dans le fichier de configuration de votre plateforme (Vercel, Netlify).",
      },
      {
        title: "Activez HSTS une fois tout le site en HTTPS",
        text: "Strict-Transport-Security: max-age=31536000, en commençant par une durée courte si certains sous-domaines fonctionnent encore en http://.",
      },
      {
        title: "Masquez la version de votre serveur",
        text: "Sur Apache, la directive ServerTokens Prod ; sur nginx, server_tokens off;. Un attaquant n'a pas besoin de savoir quelle version vous utilisez.",
      },
      {
        title: "Construisez votre CSP progressivement",
        text: "Commencez par l'en-tête Content-Security-Policy-Report-Only : il signale ce qui serait bloqué, sans rien bloquer. Listez les sources réellement utilisées (votre domaine, votre outil de statistiques, vos polices), puis passez en mode bloquant.",
      },
    ],
    fixWarning:
      "Une CSP trop stricte ajoutée d'un coup peut casser votre site : formulaires, statistiques ou paiement qui ne se chargent plus. Testez toujours en mode Report-Only avant.",

    faqs: [
      {
        q: "Mon site est sous WordPress. Puis-je ajouter ces en-têtes ?",
        a: "Oui, sans toucher au thème : par le fichier .htaccess si votre hébergeur utilise Apache, par une extension de sécurité qui gère les en-têtes, ou dans le panneau de votre hébergeur s'il le propose.",
      },
      {
        q: "Pourquoi X-XSS-Protection n'est-il pas vérifié ?",
        a: "Cet ancien en-tête n'est plus pris en charge par les navigateurs modernes. La protection contre les scripts injectés passe aujourd'hui par Content-Security-Policy.",
      },
      {
        q: "Le test est-il intrusif ?",
        a: "Non. CyberGuard lit les en-têtes renvoyés par votre page d'accueil, comme le fait chaque navigateur qui la charge. Aucune attaque n'est simulée et rien n'est modifié.",
      },
      {
        q: "Quel plan faut-il pour cette analyse ?",
        a: "HSTS, CSP et X-Frame-Options sont vérifiés dès le plan Starter à 29 €/mois. L'analyse avancée (X-Content-Type-Options, Referrer-Policy, Permissions-Policy, version serveur, CORS, cookies) et le rapport IA sont inclus à partir du plan Pro à 79 €/mois.",
      },
    ],

    related: [
      { label: "NIS2 et PME : qui est concerné, ce qui va changer, et où en est la France", href: "/blog/nis2-pme-ce-qui-change" },
      { label: "RGPD et sécurité technique : la checklist que les DPO oublient souvent", href: "/blog/rgpd-checklist-securite-technique" },
    ],
  },
];

export default checkPages;
